import React, { useState, useCallback } from 'react';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Tooltip from '@amzn/meridian/tooltip';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import { storeSubcategory } from '@components/agile/types/AgileMacroplanogramTypes';
import { ItemEntry } from '@graphql/generated/graphql';
import useApplyItemsChange from '@store/hooks/useApplyItemsChange';
import AddItemInput, { StagedItem, isSaveEnabled, buildMutationInput, mapSaveErrors } from './AddItemInput';
import { useProgramConfig } from 'src/hooks/useProgramConfig';
import { MacroplanogramScope } from 'src/types/programConfig';

// Re-export for any external consumers that imported from here
export type { StagedItem } from './AddItemInput';
export { isSaveEnabled, buildMutationInput, mapSaveErrors } from './AddItemInput';

export interface AddItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
  storeId: string;
  macroplanogramId: string;
  selectedDate: string;
  macroplanogramStartTime: number;
  macroplanogramEndTime: number;
  subcategories: storeSubcategory[];
  existingItems: ItemEntry[] | undefined;
  /** Macroplanogram-level categoryId (Phase 1 GFC only). When set, save uses
   *  it as the scope identifier instead of per-item subcategoryId. */
  categoryId?: string | null;
}

const AddItemsModal = (props: AddItemsModalProps) => {
  const {
    isOpen,
    onClose,
    onSaveSuccess,
    storeId,
    macroplanogramId,
    selectedDate,
    macroplanogramStartTime,
    macroplanogramEndTime,
    subcategories,
    existingItems,
    categoryId,
  } = props;

  const config = useProgramConfig();
  const scope: MacroplanogramScope = config.features.itemAssortment?.scope ?? 'subcategory';
  const subcategoryLabel = config.labels.subcategory;

  const [stagedItems, setStagedItems] = useState<StagedItem[]>([]);
  const [saveErrors, setSaveErrors] = useState<Map<string, string>>(new Map());
  const [globalError, setGlobalError] = useState<string | null>(null);

  const { mutateAsync, isLoading: isSaving } = useApplyItemsChange();

  const resetState = useCallback(() => {
    setStagedItems([]);
    setSaveErrors(new Map());
    setGlobalError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSave = useCallback(async () => {
    setGlobalError(null);
    setSaveErrors(new Map());

    const input = buildMutationInput(stagedItems, storeId, macroplanogramId, categoryId ?? undefined);
    const subcategoryMap = new Map(subcategories.map((s) => [s.subcategoryId, s.subcategoryName]));

    try {
      const result = await mutateAsync({ input });
      const errors = result?.applyItemsChange?.errors ?? [];

      if (errors.length > 0) {
        setSaveErrors(mapSaveErrors(errors, subcategoryMap));
      } else {
        onSaveSuccess();
        handleClose();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred while saving.';
      setGlobalError(message);
    }
  }, [stagedItems, storeId, macroplanogramId, subcategories, mutateAsync, onSaveSuccess, handleClose, categoryId]);

  const saveEnabled = isSaveEnabled(stagedItems, scope === 'subcategory') && !isSaving;

  return (
    <Modal title="Add Items" open={isOpen} onClose={handleClose} scrollContainer="modal" width="1100px">
      <Column spacing="400">
        {globalError && (
          <Alert type="error" size="small">
            {globalError}
          </Alert>
        )}

        <AddItemInput
          storeId={storeId}
          macroplanogramId={macroplanogramId}
          selectedDate={selectedDate}
          macroplanogramStartTime={macroplanogramStartTime}
          macroplanogramEndTime={macroplanogramEndTime}
          subcategories={subcategories}
          existingItems={existingItems}
          stagedItems={stagedItems}
          onStagedItemsChange={setStagedItems}
          saveErrors={saveErrors}
        />
      </Column>

      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button type="secondary" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          {!saveEnabled ? (
            <Tooltip
              position="top"
              title={
                scope === 'subcategory'
                  ? `Add items and select ${subcategoryLabel.toLowerCase()} to save`
                  : 'Add items to save'
              }
            >
              <span tabIndex={0}>
                <Button type="primary" disabled>
                  Save
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button type="primary" onClick={handleSave}>
              {isSaving ? <Loader type="circular" size="small" /> : 'Save'}
            </Button>
          )}
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default AddItemsModal;
